// App-wide constants and content (not environment config — that lives in src/core/config/app.ts).
import type { ILocationFixedDataType } from "@/src/shared/types/locations";

export const ITEMS_PER_PAGE = 20;

export const PLACE_HOLDER_IMAGE = "/images/place-holder-brand-image.png";

// Shown in place of a message an admin has hidden. The `message_hidden` WS frame
// carries the same text, but the conversation-list endpoint still returns the
// original body next to `is_hidden: true`, so the client masks it from the flag.
export const HIDDEN_MESSAGE_BODY = "(This message is hidden by Travela)";

// Global currency configuration. Change CURRENCY_CODE to switch the
// currency shown across the app (e.g. "USD", "EUR"); CURRENCY_LOCALE
// controls thousands grouping used by `formatPrice`.
export const CURRENCY_CODE = "BDT";

export const CURRENCY_LOCALE = "en-BD";

// Short glyph used where the design shows a symbol rather than the code
// (price inputs, range sliders). Switch alongside CURRENCY_CODE.
export const CURRENCY_SYMBOL = "৳";

export const visibleSmallerSearchBarRoute = [
  "/host-details",
  "/blogs",
  "/reliable-stays-across-bangladesh",
  "/hotels",
  "/popular-locations",
  "/rooms",
  "/earn-by-hosting",
  "/terms-conditions",
  "/refund-policy",
  "/privacy-policy",
  "/contact-us",
  "/about",
  "/listing/",
  "/s/",
  "/profile",
  "/booking",
  "/notification",
  "/inbox",
  "/deshboard",
  "/listing",
  "/hotel",
  "/explore-more",
  "/faq",
  "/stays",
  "/wishlists",
  "/wallet",
  "/search",
];

export const fixedLocations: ILocationFixedDataType[] = [
  // Dhaka locations
  {
    id: 1,
    name: "mohammadpur",
    name_bn: "মোহাম্মদপুর",
    lat: 23.7661639,
    lng: 90.3588734,
    order: 0,
    gradient: "from-pink-500 via-rose-500 to-red-500",
    popularity: 95,
    rating: 4.8,
    properties: 1240,
    priceRange: "$25-85",
    type: "popular",
    parent: "dhaka",
    path: "mohammadpur",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 3,
    name: "dhaka",
    name_bn: "ঢাকা",
    lat: 23.7494629,
    lng: 90.3665972,
    order: 1,
    gradient: "from-blue-600 via-purple-600 to-pink-600",
    popularity: 98,
    rating: 4.9,
    properties: 2850,
    priceRange: "$30-120",
    type: "city",
    parent: "dhaka",
    path: "dhaka",
    hotel: {
      description:
        "Hotel Booking in Dhaka with Travela offers a smooth way to book hotels in Dhaka for business and leisure travel. The platform supports online hotel booking Dhaka with verified rooms, clear details and secure reservations. Hotel booking in Dhaka becomes simple through trusted listings and quick confirmation. Travela focuses on reliable stays, transparent pricing and dependable customer support for every guest.",
      h1: "Hotel Booking in Dhaka, Bangladesh",
      h2: "Hotel Booking in Dhaka for Comfortable and Reliable Stays",
      metaDescription:
        "Hotel booking in Dhaka made simple with trusted listings, secure online hotel booking Dhaka and instant stay confirmation.",
      metaKeyword: [
        "hotel booking in dhaka",
        "online hotel booking dhaka",
        "hotel booking dhaka",
        "book hotels in dhaka",
        "hotel booking sites in bangladesh",
      ],
      pageTitle: "Hotel Booking in Dhaka, Bangladesh",
      url: "",
    },
    room: {
      description:
        "Room rent in Dhaka depends on location, room size and nearby facilities. Popular areas show higher demand due to offices and universities. Therefore, prices change frequently. Travela helps users view updated room rent in Dhaka with clear details. As a result, renters can compare options faster. Moreover, the platform supports better rental decisions with reliable listings.",
      h1: "Room Rent in Dhaka",
      h2: "Find the best room rent deals in Dhaka",
      metaDescription:
        "Book Room Rent in Dhaka with modern rooms and comfort. Ideal for families, professionals or short-term stays.",
      metaKeyword: [
        "room rent in dhaka",
        "Room Rent Dhaka",
        "single room rent in dhaka",
        "hourly room rent in dhaka",
      ],
      pageTitle: "Room Rent in Dhaka, Bangladesh",
      url: "",
    },
  },
  {
    id: 578,
    name: "dhanmondi west",
    name_bn: "পশ্চিম ধানমন্ডি",
    lat: 23.7494629,
    lng: 90.3665972,
    order: 3,
    gradient: "from-violet-500 via-purple-500 to-pink-500",
    popularity: 92,
    rating: 4.7,
    properties: 890,
    priceRange: "$35-95",
    type: "popular",
    parent: "dhaka",
    path: "dhanmondi",
    hotel: {
      description:
        "Travela offers reliable hotel booking in Dhanmondi for business and leisure travelers. Dhanmondi is a prime area in Dhaka, Bangladesh, known for shopping, dining and medical centers. With Travela, users can compare hotel rates, locations and guest reviews in one place. Hotel booking in Dhanmondi becomes simple, fast and secure with trusted listings and updated availability.",
      h1: "Hotel Booking in Dhanmondi, Dhaka, Bangladesh",
      h2: "Find stays in the most popular hotels in Dhanmondi",
      metaDescription:
        "Book the best Hotel Booking in Dhanmondi with comfort and value. Top-rated hotel in Dhanmondi area for business or family stay.",
      metaKeyword: [
        "hotels in dhaka dhanmondi",
        "hotel booking in dhanmondi",
        "hotel near dhanmondi",
        "dhanmondi hotel list",
        "hotel in dhanmondi area",
        "flat rent in dhanmondi",
      ],
      pageTitle: "Hotel Booking in Dhanmondi",
      url: "",
    },
    room: {
      description:
        "Travela provides trusted room rent in Dhanmondi for students, families and business travelers. Dhanmondi is a well-known area in Dhaka, Bangladesh, close to schools, hospitals and shopping centers. Users can find short stay and long stay room rent in Dhanmondi with clear pricing and location details. Travela makes room booking simple, secure and convenient for every guest.",
      h1: "Room Rent in Dhanmondi, Dhaka, Bangladesh",
      h2: "Find short and long stay room rent in Dhanmondi",
      metaDescription:
        "Book Room Rent in Dhanmondi with secure and cozy living. Trusted room for rent in Dhanmondi for families or professionals.",
      metaKeyword: [
        "room for rent in dhanmondi",
        "1 room rent in dhanmondi",
        "2 room rent in dhanmondi",
        "single room rent in dhanmondi",
        "2 room flat rent in dhanmondi",
      ],
      pageTitle: "Room Rent in Dhanmondi",
      url: "",
    },
  },
  {
    id: 40,
    name: "dhanmondi north",
    name_bn: "উত্তর ধানমন্ডি",
    lat: 23.752851873048,
    lng: 90.381560283699,
    order: 4,
    gradient: "from-amber-400 via-orange-500 to-red-500",
    popularity: 89,
    rating: 4.5,
    properties: 720,
    priceRange: "$30-90",
    type: "popular",
    parent: "dhaka",
    path: "dhanmondi",
    hotel: {
      description:
        "Travela offers reliable hotel booking in Dhanmondi for business and leisure travelers. Dhanmondi is a prime area in Dhaka, Bangladesh, known for shopping, dining and medical centers. With Travela, users can compare hotel rates, locations and guest reviews in one place. Hotel booking in Dhanmondi becomes simple, fast and secure with trusted listings and updated availability.",
      h1: "Hotel Booking in Dhanmondi, Dhaka, Bangladesh",
      h2: "Find stays in the most popular hotels in Dhanmondi",
      metaDescription:
        "Book the best Hotel Booking in Dhanmondi with comfort and value. Top-rated hotel in Dhanmondi area for business or family stay.",
      metaKeyword: [
        "hotels in dhaka dhanmondi",
        "hotel booking in dhanmondi",
        "hotel near dhanmondi",
        "dhanmondi hotel list",
        "hotel in dhanmondi area",
        "flat rent in dhanmondi",
      ],
      pageTitle: "Hotel Booking in Dhanmondi",
      url: "",
    },
    room: {
      description:
        "Travela provides trusted room rent in Dhanmondi for students, families and business travelers. Dhanmondi is a well-known area in Dhaka, Bangladesh, close to schools, hospitals and shopping centers. Users can find short stay and long stay room rent in Dhanmondi with clear pricing and location details. Travela makes room booking simple, secure and convenient for every guest.",
      h1: "Room Rent in Dhanmondi, Dhaka, Bangladesh",
      h2: "Find short and long stay room rent in Dhanmondi",
      metaDescription:
        "Book Room Rent in Dhanmondi with secure and cozy living. Trusted room for rent in Dhanmondi for families or professionals.",
      metaKeyword: [
        "room for rent in dhanmondi",
        "1 room rent in dhanmondi",
        "2 room rent in dhanmondi",
        "single room rent in dhanmondi",
        "2 room flat rent in dhanmondi",
      ],
      pageTitle: "Room Rent in Dhanmondi",
      url: "",
    },
  },
  {
    id: 12,
    name: "gulshan",
    name_bn: "গুলশান",
    lat: 23.789262566692,
    lng: 90.413849763572,
    order: 5,
    gradient: "from-emerald-400 via-teal-500 to-cyan-600",
    popularity: 94,
    rating: 4.8,
    properties: 1150,
    priceRange: "$45-150",
    type: "popular",
    parent: "dhaka",
    path: "gulshan",
    hotel: {
      description:
        "Travela offers reliable hotels in Gulshan, Dhaka, Bangladesh for business and leisure travelers. Users can book a hotel near Gulshan with clear details and smooth steps. The platform includes options from the best hotel in Gulshan Dhaka to budget stays. It also features a helpful Gulshan 2 hotel list, making hotel booking in Gulshan simple and convenient.",
      h1: "Booking Cheap Hotel in Gulshan, Dhaka",
      h2: "Best Hotel in Gulshan Dhaka for Every Stay",
      metaDescription:
        "Hotels in Gulshan Dhaka offering hotel near Gulshan choices and a reliable Gulshan 2 hotel list for all travelers.",
      metaKeyword: [
        "hotels in Gulshan",
        "hotel near gulshan",
        "best hotel in gulshan dhaka",
        "gulshan 2 hotel list",
      ],
      pageTitle: "Hotels in Gulshan Dhaka, Bangladesh",
      url: "",
    },
    room: {
      description:
        "Travela offers reliable room rent in Gulshan for various needs and budgets. Users can choose single room rent in Gulshan with clear details and simple booking steps. The platform also includes cheap room rent in Gulshan and short-term room rent in Gulshan for flexible stays. It helps users secure comfortable rooms in Gulshan with ease and convenience.",
      h1: "Room Rent in Gulshan, Dhaka, Bangladesh",
      h2: "Best Room Rent in Gulshan for Every Stay",
      metaDescription:
        "Room rent in Gulshan offering cheap room rent in Gulshan choices and secure single room rent in Gulshan stays.",
      metaKeyword: [
        "room Rent in gulshan",
        "single room rent in gulshan",
        "cheap room rent in gulshan",
        "short-term room rent in gulshan",
      ],
      pageTitle: "Room Rent in Gulshan, Dhaka",
      url: "",
    },
  },
  {
    id: 120,
    name: "bashundhara block b",
    name_bn: "বসুন্ধরা ব্লক বি",
    lat: 23.814884324923,
    lng: 90.429573208094,
    order: 6,
    gradient: "from-green-400 via-emerald-500 to-teal-600",
    popularity: 86,
    rating: 4.4,
    properties: 480,
    priceRange: "$25-80",
    type: "city",
    parent: "dhaka",
    path: "bashundhara-block-b",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription:
        "Book Room Rent in Dhaka with modern rooms and comfort. Ideal for families, professionals or short-term stays.",
      metaKeyword: [],
      pageTitle: "Hotel Booking in Bashundhara B Block",
      url: "",
    },
    room: {
      description:
        "Travela offers reliable room rent in Bashundhara B Block for students, professionals and families. This area in Dhaka provides easy access to offices, schools and shopping centers. Users can view room rent in Bashundhara B Block with clear pricing, facilities and location details. Travela ensures fast, secure and convenient booking for both short-term and long-term stays.",
      h1: "Room Rent in Bashundhara B Block, Dhaka",
      h2: "Find room rent options in Bashundhara B Block",
      metaDescription:
        "Book Room Rent in Bashundhara B Block with modern rooms and comfort. Trusted bashundhara b block room rent for families or professionals.",
      metaKeyword: [
        "room rent in dhaka",
        "Room Rent Dhaka",
        "single room rent in dhaka",
        "hourly room rent in dhaka",
      ],
      pageTitle: "Room Rent in Bashundhara B Block",
      url: "",
    },
  },
  {
    id: 215,
    name: "bashundhara residential area",
    name_bn: "বসুন্ধরা আবাসিক এলাকা",
    lat: 23.821885838406,
    lng: 90.434770323336,
    order: 7,
    gradient: "from-indigo-500 via-blue-600 to-cyan-600",
    popularity: 91,
    rating: 4.6,
    properties: 820,
    priceRange: "$40-110",
    type: "city",
    parent: "dhaka",
    path: "bashundhara-residential-area",
    hotel: {
      description:
        "Travela offers reliable hotel booking in Bashundhara Residential Area for business and family travelers. Bashundhara Residential Area is a growing zone in Dhaka, Bangladesh, near shopping malls, universities, and offices. Users can compare hotels in Bashundhara Residential Area with updated rates and room details. Travela makes hotel booking simple, secure and convenient with verified listings and easy search options.",
      h1: "Hotel Booking in Bashundhara Residential Area",
      h2: "Find stays in the most popular hotels in Bashundhara Residential Area",
      metaDescription:
        "Stay at a Hotel in Bashundhara Residential Area with modern rooms and comfort. Premium hotel in Bashundhara for business or family stays.",
      metaKeyword: [
        "hotel in bashundhara residential area",
        "hotel in bashundhara",
        "cheap hotels in bashundhara residential area dhaka",
        "bashundhara residential area hotel",
      ],
      pageTitle: "Hotel in Bashundhara Residential Area",
      url: "",
    },
    room: {
      description:
        "Travela provides reliable room rent in Bashundhara Residential Area for students, families and professionals. This area in Dhaka, Bangladesh offers easy access to universities, shopping centers and offices. Users can browse room rent in Bashundhara with clear details on pricing, facilities and location. Travela makes booking simple, secure and fast for both short-term and long-term stays.",
      h1: "Room Rent in Bashundhara Residential Area",
      h2: "Find affordable room rent options in Bashundhara Residential Area",
      metaDescription:
        "Room Rent in Bashundhara with modern facilities. Ideal room rent in Bashundhara Residential Area for families or professionals.",
      metaKeyword: [
        "room rent in bashundhara",
        "single room rent in bashundhara",
        "1 room flat in bashundhara city",
        "room rent in bashundhara dhaka",
      ],
      pageTitle: "Room Rent in Bashundhara",
      url: "",
    },
  },
  {
    id: 8,
    name: "mirpur",
    name_bn: "মিরপুর",
    lat: 23.8223486,
    lng: 90.3654204,
    order: 10,
    gradient: "from-purple-500 via-indigo-600 to-blue-700",
    popularity: 90,
    rating: 4.6,
    properties: 950,
    priceRange: "$20-70",
    type: "popular",
    parent: "dhaka",
    path: "mirpur",
    hotel: {
      description:
        "Hotel booking Mirpur offers a simple way to arrange stays in a busy part of Dhaka. Visitors can choose from various hotels near Mirpur and hotels in Mirpur Dhaka that suit different needs. The area provides easy access to transport, dining and daily services. It is a suitable location for both short visits and longer stays with reliable accommodation choices.",
      h1: "Hotel Booking Mirpur for Comfortable Stay Options",
      h2: "Top Hotels Near Mirpur and Hotels in Mirpur Dhaka",
      metaDescription:
        "Get smooth hotel booking Mirpur services with a range of hotels near Mirpur and comfortable hotels in Mirpur Dhaka.",
      metaKeyword: [
        "hotel booking in mirpur",
        "hotel booking mirpur",
        "hotels near mirpur",
        "hotels in mirpur dhaka",
      ],
      pageTitle: "Hotel Booking in Mirpur, Dhaka",
      url: "",
    },
    room: {
      description:
        "Room rent in Mirpur is a suitable option for people looking for a convenient place to stay in Dhaka. The area offers many room rent Mirpur choices with access to transport, shops and daily services. Mirpur room rent listings include different types of rooms suited for students, professionals and families. It provides a balanced living environment with essential facilities nearby and flexible rental options.",
      h1: "Room Rent in Mirpur for Comfortable Living",
      h2: "Comfortable Room Rent in Mirpur Dhaka Areas",
      metaDescription:
        "Choose room rent in Mirpur with convenient locations and a range of room rent Mirpur available now.",
      metaKeyword: [
        "room rent in mirpur",
        "room rent mirpur",
        "mirpur room rent",
      ],
      pageTitle: "Room Rent in Mirpur, Dhaka",
      url: "",
    },
  },
  {
    id: 6,
    name: "uttara",
    name_bn: "উত্তরা",
    lat: 23.8758547,
    lng: 90.3795438,
    order: 11,
    gradient: "from-teal-400 via-cyan-500 to-blue-600",
    popularity: 88,
    rating: 4.5,
    properties: 780,
    priceRange: "$35-100",
    type: "popular",
    parent: "dhaka",
    path: "uttara",
    hotel: {
      description:
        "Travela provides reliable hotel booking in Uttara, Dhaka, Bangladesh for business and leisure travelers. Users can complete Uttara hotel booking with simple steps and clear details. The platform makes it easy to book hotel in Uttara with options for short and long stays. It also features the best hotel booking in Uttara, helping users choose suitable stays with comfort and convenience.",
      h1: "Hotel Booking in Uttara, Dhaka, Bangladesh",
      h2: "Best Hotel Booking in Uttara for Every Stay",
      metaDescription:
        "Hotel booking in Uttara with smooth booking options to book hotel in Uttara and reliable Uttara hotel booking services.",
      metaKeyword: [
        "Hotel Booking in Uttara",
        "Uttara hotel booking",
        "Book hotel in Uttara",
        "Best hotel booking in Uttara",
      ],
      pageTitle: "Hotel Booking in uttara, Dhaka",
      url: "",
    },
    room: {
      description:
        "Travela presents room rent in Uttara for short-term stay with clear price and location details. It covers options for students and professionals seeking a comfortable place. Each listing includes nearby facilities and easy access to transport. Room rent in Uttara varies by area and features, helping tenants choose a suitable option without confusion.",
      h1: "Room Rent in Uttara for Short-Term Stay",
      h2: "Room Rent in Uttara with Price and Location Details",
      metaDescription:
        "Room rent in Uttara listings with updated prices, nearby facilities and easy contact details for tenants seeking a suitable place.",
      metaKeyword: [
        "room rent in Uttara",
        "Uttara room rent",
        "single room rent in Uttara",
        "room rent in Uttara for bachelor",
      ],
      pageTitle: "Room Rent in Uttara, Dhaka",
      url: "",
    },
  },
  {
    id: 66,
    name: "banani",
    name_bn: "বনানী",
    lat: 23.795787815194,
    lng: 90.408349223435,
    order: 12,
    gradient: "from-green-500 via-emerald-600 to-teal-700",
    popularity: 93,
    rating: 4.7,
    properties: 670,
    priceRange: "$50-140",
    type: "popular",
    parent: "dhaka",
    path: "banani",
    hotel: {
      description:
        "Hotels in Banani provide a suitable stay option in one of Dhaka well-known areas. Visitors can choose from various hotels in Banani Dhaka that offer access to dining, shopping and transport. Cheap hotels in Banani Dhaka are also available for those seeking budget-friendly choices. The area supports both short and long stays with a range of accommodation options and essential services nearby.",
      h1: "Hotel Booking in Banani, Dhaka",
      h2: "Hotels in Banani Dhaka for Comfortable and Convenient Stays",
      metaDescription:
        "Hotels in Banani include premium and cheap hotels in Banani Dhaka with easy access to top hotels in Banani Dhaka locations.",
      metaKeyword: [
        "hotels in banani",
        "hotels in banani dhaka",
        "cheap hotels in banani dhaka",
      ],
      pageTitle: "Hotel Booking in Banani, Dhaka",
      url: "",
    },
    room: {
      description:
        "Room rent in Banani is a suitable choice for those looking to stay in a well-connected area of Dhaka. Options include single room rent in Banani Dhaka and 1 bedroom apartments for rent in Banani that meet different living needs. Dhaka Banani room rent listings provide access to offices, dining and transport. The area supports convenient living with various rental choices and essential services nearby.",
      h1: "Room Booking in Banani, Dhaka",
      h2: "Room Rent in Banani for Comfortable Living",
      metaDescription:
        "Room rent in Banani offers options like dhaka banani room rent and single room rent in Banani Dhaka in prime locations.",
      metaKeyword: [
        "room rent in banani",
        "single room rent in banani dhaka",
        "1 bedroom apartments for rent in banani",
        "dhaka banani room rent",
      ],
      pageTitle: "Room Rent in Banani, Dhaka",
      url: "",
    },
  },
  {
    id: 78,
    name: "baridhara",
    name_bn: "বারিধারা",
    lat: 23.7998984,
    lng: 90.420766,
    order: 13,
    gradient: "from-blue-400 via-cyan-500 to-teal-600",
    popularity: 89,
    rating: 4.6,
    properties: 520,
    priceRange: "$55-160",
    type: "city",
    parent: "dhaka",
    path: "baridhara",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  // Cox's Bazar locations
  {
    id: 42,
    name: "cox's bazar",
    name_bn: "কক্সবাজার",
    lat: 21.4272283,
    lng: 92.0058074,
    order: 127,
    gradient: "from-orange-400 via-red-500 to-pink-500",
    popularity: 96,
    rating: 4.9,
    properties: 1850,
    priceRange: "$40-200",
    type: "beach",
    path: "coxs-bazar",
    hotel: {
      description:
        "Hotel booking in Cox’s Bazar is simple with clear and updated information. Travela helps travelers choose the right stay near the beach and nearby areas. This guide covers popular hotel booking options in Cox’s Bazar and surrounding locations. It includes details on room types, prices and locations. Short and clear details support smooth hotel booking for families, couples and business travelers.",
      h1: "Hotel Booking in Cox's Bazar, Bangladesh",
      h2: "Most Popular Hotel Bookings in Cox’s Bazar and Surrounding Areas",
      metaDescription:
        "Hotel booking in Cox's Bazar made simple with trusted options for online hotel booking Cox's Bazar and smooth hotel room booking.",
      metaKeyword: [
        "cox bazar hotel booking",
        "hotel booking in cox's bazar",
        "online hotel booking cox's bazar",
        "cox's bazar hotel room booking",
      ],
      pageTitle: "Hotel Booking in Cox's Bazar",
      url: "",
    },
    room: {
      description:
        "Room rent in Cox’s Bazar varies by location, season and hotel type. Travela presents clear details for travelers planning a stay near the beach or city areas. This guide shares the latest Cox’s Bazar hotel room price list for luxury hotels, mid-range stays and budget rooms. Prices are updated to help guests plan with confidence. Simple information makes booking decisions easier for families, couples and solo travelers.",
      h1: "Room Rent in Cox’s Bazar, Bangladesh",
      h2: "Find the latest cox's bazar hotel room price list for all budgets",
      metaDescription:
        "Get the latest cox's bazar room price with a detailed cox's bazar hotel room price list for family and couple stays round the year.",
      metaKeyword: [
        "room rent in cox's bazar",
        "cox's bazar room price",
        "couple room in cox's bazar bangladesh",
        "cox's bazar hotel room price list",
      ],
      pageTitle: "Room Rent in Cox's Bazar",
      url: "",
    },
  },
  {
    id: 396,
    name: "coxs bazar",
    name_bn: "কক্সবাজার, বাংলাদেশে",
    lat: 21.4272283,
    lng: 92.0058074,
    order: 127,
    gradient: "from-blue-500 via-teal-500 to-green-500",
    popularity: 94,
    rating: 4.8,
    properties: 1200,
    priceRange: "$35-180",
    type: "beach",
    path: "coxs-bazar",
    hotel: {
      description:
        "Hotel booking in Cox’s Bazar is simple with clear and updated information. Travela helps travelers choose the right stay near the beach and nearby areas. This guide covers popular hotel booking options in Cox’s Bazar and surrounding locations. It includes details on room types, prices and locations. Short and clear details support smooth hotel booking for families, couples and business travelers.",
      h1: "Hotel Booking in Cox's Bazar, Bangladesh",
      h2: "Most Popular Hotel Bookings in Cox’s Bazar and Surrounding Areas",
      metaDescription:
        "Hotel booking in Cox's Bazar made simple with trusted options for online hotel booking Cox's Bazar and smooth hotel room booking.",
      metaKeyword: [
        "cox bazar hotel booking",
        "hotel booking in cox's bazar",
        "online hotel booking cox's bazar",
        "cox's bazar hotel room booking",
      ],
      pageTitle: "Hotel Booking in Cox's Bazar",
      url: "",
    },
    room: {
      description:
        "Room rent in Cox’s Bazar varies by location, season and hotel type. Travela presents clear details for travelers planning a stay near the beach or city areas. This guide shares the latest Cox’s Bazar hotel room price list for luxury hotels, mid-range stays and budget rooms. Prices are updated to help guests plan with confidence. Simple information makes booking decisions easier for families, couples and solo travelers.",
      h1: "Room Rent in Cox’s Bazar, Bangladesh",
      h2: "Find the latest cox's bazar hotel room price list for all budgets",
      metaDescription:
        "Get the latest cox's bazar room price with a detailed cox's bazar hotel room price list for family and couple stays round the year.",
      metaKeyword: [
        "room rent in cox's bazar",
        "cox's bazar room price",
        "couple room in cox's bazar bangladesh",
        "cox's bazar hotel room price list",
      ],
      pageTitle: "Room Rent in Cox's Bazar",
      url: "",
    },
  },
  {
    id: 208582,
    name: "chattogram",
    name_bn: "চট্টগ্রাম",
    lat: 22.356851,
    lng: 91.7831819,
    order: 127,
    gradient: "from-orange-400 via-red-500 to-pink-500",
    popularity: 96,
    rating: 4.9,
    properties: 1850,
    priceRange: "$40-200",
    type: "new-town",
    path: "chattogram",
    hotel: {
      description:
        "Travela offers reliable hotel booking in Chattogram, Bangladesh for all types of travelers. Users can choose a hotel in Chattogram that fits their needs, from business stays to family trips. The platform lists top Chattogram hotel in the city options with clear details and simple booking steps. It helps users plan stays in Chattogram city with comfort and convenience.",
      h1: "Hotel Booking in Chattogram, Bangladesh",
      h2: "Top Hotels in Chattogram City for Every Stay",
      metaDescription:
        "Hotel booking in Chattogram offering quality hotel in Chattogram stays and reliable Chattogram hotel in the city choices.",
      metaKeyword: ["hotel in chattogram", "chattogram hotel in the city"],
      pageTitle: "Hotels in Chattogram",
      url: "",
    },
    room: {
      description:
        "Travela provides trusted room rent in Chattogram, Bangladesh for different needs and budgets. Users can choose from verified chattogram room rent listings with clear details and simple booking steps. It includes options for single room rent in Chattogram, suitable for students, professionals and short stays. The platform helps users secure comfortable rooms in Chattogram city with ease and convenience.",
      h1: "Room Rent in Chattogram, Bangladesh",
      h2: "Best Chattogram Room Rent Options for Every Stay",
      metaDescription:
        "Room rent in Chattogram featuring updated chattogram room rent listings and comfortable single room rent in Chattogram stays.",
      metaKeyword: [
        "rent room in Chattogram",
        "chattogram room rent",
        "single room rent in Chattogram",
      ],
      pageTitle: "Room Rent in Chattogram",
      url: "",
    },
  },
  // Rajshahi
  {
    id: 230,
    name: "rajshahi",
    name_bn: "রাজশাহী",
    lat: 24.3745146,
    lng: 88.604166,
    order: 128,
    gradient: "from-indigo-400 via-blue-500 to-cyan-500",
    popularity: 85,
    rating: 4.6,
    properties: 720,
    priceRange: "$20-100",
    type: "city",
    path: "rajshahi",
    hotel: {
      description:
        "Hotel booking in Rajshahi provides travelers with a convenient way to arrange a comfortable stay. The city offers a range of hotels with clean rooms and essential amenities. Visitors can enjoy a calm environment along with reliable service during their visit. This option suits both short and long stays, making Rajshahi a practical choice for those seeking comfort and convenience.",
      h1: "Hotel Booking in Rajshahi, Bangladesh",
      h2: "Simple and Reliable Hotel Booking in Rajshahi",
      metaDescription:
        "Hotel booking in Rajshahi offers clean rooms, good service and convenient locations for travelers seeking a comfortable stay.",
      metaKeyword: ["hotel booking in rajshahi", "rajshahi hotel booking"],
      pageTitle: "Hotel Booking in Rajshahi",
      url: "",
    },
    room: {
      description:
        "Room rent in Rajshahi gives individuals a practical option for comfortable living in the city. Various rooms are available with essential facilities and clean environments. Tenants can enjoy convenient locations that provide easy access to daily needs. This option is suitable for students, professionals and visitors who require a simple and comfortable place to stay in Rajshahi.",
      h1: "Room Rent in Rajshahi, Bangladesh",
      h2: "Easy and Reliable Room Rent in Rajshahi",
      metaDescription:
        "Room rent in Rajshahi offers clean and comfortable spaces with essential facilities for short or long-term stays in the city.",
      metaKeyword: ["room rent in rajshahi", "rajshahi room rent"],
      pageTitle: "Room Rent in Rajshahi",
      url: "",
    },
  },

  // Rangpur
  {
    id: 231,
    name: "rangpur",
    name_bn: "রংপুর",
    lat: 25.766091812501,
    lng: 89.210379123688,
    order: 129,
    gradient: "from-yellow-400 via-amber-500 to-red-500",
    popularity: 78,
    rating: 4.5,
    properties: 610,
    priceRange: "$18-90",
    type: "city",
    path: "rangpur",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },

  // Khulna
  {
    id: 232,
    name: "khulna",
    name_bn: "খুলনা",
    lat: 22.845641,
    lng: 89.5403279,
    order: 130,
    gradient: "from-teal-400 via-emerald-500 to-green-500",
    popularity: 82,
    rating: 4.6,
    properties: 800,
    priceRange: "$20-110",
    type: "city",
    path: "khulna",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },

  // Barishal
  {
    id: 233,
    name: "barishal",
    name_bn: "বরিশাল",
    lat: 22.7010021,
    lng: 90.3534511,
    order: 131,
    gradient: "from-orange-400 via-amber-500 to-yellow-500",
    popularity: 79,
    rating: 4.5,
    properties: 560,
    priceRange: "$18-95",
    type: "city",
    path: "barishal",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },

  // Mymensingh
  {
    id: 234,
    name: "mymensingh",
    name_bn: "ময়মনসিংহ",
    lat: 24.7471492,
    lng: 90.4202734,
    order: 132,
    gradient: "from-indigo-400 via-purple-500 to-pink-500",
    popularity: 74,
    rating: 4.4,
    properties: 430,
    priceRange: "$18-85",
    type: "city",
    path: "mymensingh",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },

  // Sylhet locations
  {
    id: 43,
    name: "sylhet",
    name_bn: "সিলেট",
    lat: 24.8949294,
    lng: 91.8687063,
    order: 127,
    gradient: "from-green-500 via-emerald-600 to-teal-700",
    popularity: 91,
    rating: 4.7,
    properties: 1350,
    priceRange: "$25-95",
    type: "city",
    path: "sylhet",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 540,
    name: "sylhet",
    name_bn: "সিলেট, বাংলাদেশ",
    lat: 24.8949294,
    lng: 91.8687063,
    order: 127,
    gradient: "from-purple-500 via-pink-500 to-red-500",
    popularity: 88,
    rating: 4.5,
    properties: 890,
    priceRange: "$20-80",
    type: "city",
    path: "sylhet",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 553,
    name: "khadimnagar national park sylhet",
    name_bn: "খাদিমনগর জাতীয় উদ্যান, সিলেট, বাংলাদেশ",
    lat: 24.9569894,
    lng: 91.9343555,
    order: 127,
    gradient: "from-lime-400 via-green-500 to-emerald-600",
    popularity: 85,
    rating: 4.4,
    properties: 280,
    priceRange: "$18-65",
    type: "city",
    path: "khadimnagar-national-park-sylhet",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },

  // Purbachal locations (considered part of Dhaka metropolitan area, add parent)
  {
    id: 650,
    name: "purbachal dhaka",
    name_bn: "পূর্বাচল, ঢাকা, বাংলাদেশ",
    lat: 23.8840556,
    lng: 90.5002973,
    order: 127,
    gradient: "from-indigo-400 via-purple-500 to-pink-600",
    popularity: 87,
    rating: 4.5,
    properties: 650,
    priceRange: "$35-110",
    type: "new-town",
    parent: "dhaka",
    path: "purbachal-dhaka",
    hotel: {
      description:
        "Hotel Booking in Purbachal City with Travela provides a reliable way to book hotels in Purbachal. The platform supports hotel booking in Purbachal City with verified listings and clear room details. Travelers can choose a quality hotel in Purbachal City for business or leisure stays. Travela focuses on secure reservations, transparent pricing and helpful customer support.",
      h1: "Hotel Booking in Purbachal City, Dhaka",
      h2: "Hotel Booking in Purbachal City for Comfortable and Secure Stays",
      metaDescription:
        "Hotel booking in Purbachal City with trusted hotels in Purbachal, clear room details and secure online reservation support.",
      metaKeyword: ["hotel in purbachal city", "hotels in purbachal"],
      pageTitle: "Hotel Booking in Purbachal City",
      url: "",
    },
    room: {
      description:
        "Room Rent in Purbachal City with Travela offers a simple way to choose rooms and apartments for comfortable living. The platform supports room rent in Purbachal City with verified listings and clear details. Travelers and residents can select room rent in Purbachal based on location and needs. Travela focuses on reliable options, transparent terms and smooth booking support for a stress free stay.",
      h1: "Room Rent in Purbachal City, Dhaka",
      h2: "Room Rent in Purbachal City for Comfortable Living",
      metaDescription:
        "Room rent in Purbachal City with flexible options, including apartments for rent in Purbachal and secure booking support.",
      metaKeyword: [
        "room rent in purbachal",
        "1 bedroom apartments for rent in purbachal",
        "2 bedroom apartments for rent in purbachal",
      ],
      pageTitle: "Room Rent in Purbachal City",
      url: "",
    },
  },
  {
    id: 306,
    name: "purabachal new town",
    name_bn: "পূর্বাচল নিউ টাউন",
    lat: 23.84671419529,
    lng: 90.496218985892,
    order: 49,
    gradient: "from-yellow-400 via-orange-500 to-red-600",
    popularity: 84,
    rating: 4.3,
    properties: 420,
    priceRange: "$30-95",
    type: "new-town",
    parent: "dhaka",
    path: "purabachal-new-town",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 306,
    name: "purabachal new town",
    name_bn: "পূর্বাচল নিউ টাউন",
    lat: 23.84671419529,
    lng: 90.496218985892,
    order: 49,
    parent: "dhaka",
    path: "purabachal-new-town",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 650,
    name: "purbachal, dhaka",
    name_bn: "পূর্বাচল, ঢাকা, বাংলাদেশ",
    lat: 23.8840556,
    lng: 90.5002973,
    order: 127,
    parent: "dhaka",
    path: "purbachal-dhaka",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 589,
    name: "puran dhaka",
    name_bn: "পুরান ঢাকা",
    lat: 23.717644300422,
    lng: 90.398974571648,
    order: 127,
    parent: "dhaka",
    path: "puran-dhaka",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 883,
    name: "300 feet, purbachal, dhaka",
    name_bn: "৩০০ ফিট, পূর্বাচল, ঢাকা",
    lat: 23.8710759,
    lng: 90.5074562,
    order: 127,
    parent: "dhaka",
    path: "300-feet-purbachal-dhaka",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 864,
    name: "green university, purbachal american city",
    name_bn: "গ্রিন ইউনিভার্সিটি অব বাংলাদেশ, পূর্বাচল আমেরিকান সিটি",
    lat: 23.8295015,
    lng: 90.5663123,
    order: 127,
    parent: "dhaka",
    path: "green-university-purbachal-american-city",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 884,
    name: "state university, purbachal new town project",
    name_bn:
      "স্টেট ইউনিভার্সিটি অফ বাংলাদেশ, পূর্বাচল নিউ টাউন প্রকল্প, বাংলাদেশ",
    lat: 23.8390399,
    lng: 90.5507956,
    order: 127,
    parent: "dhaka",
    path: "state-university-purbachal-new-town-project",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 877,
    name: "dig muhammad abul faiz, 300 feet road, purbachal new town project, dhaka",
    name_bn:
      "ডিআইজি মোহাম্মদ আবুল ফয়েজ, ৩০০ ফিট রোড, পূর্বাচল নিউ টাউন প্রকল্প, ঢাকা",
    lat: 23.834136,
    lng: 90.4480969,
    order: 127,
    parent: "dhaka",
    path: "dig-muhammad-abul-faiz-300-feet-road-purbachal-new-town-project-dhaka",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
  {
    id: 870,
    name: "pohs-1, police officers housing society-1, 300 feet road, purbachal new town project, dhaka",
    name_bn:
      "POHS-১, পুলিশ অফিসার্স হাউজিং সোসাইটি-১, ৩০০ ফিট রোড, পূর্বাচল নিউ টাউন প্রকল্প, ঢাকা",
    lat: 23.834136,
    lng: 90.4480969,
    order: 127,
    parent: "dhaka",
    path: "pohs-1-police-officers-housing-society-1-300-feet-road-purbachal-new-town-project-dhaka",
    hotel: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
    room: {
      description: "",
      h1: "",
      h2: "",
      metaDescription: "",
      metaKeyword: [],
      pageTitle: "",
      url: "",
    },
  },
];

export const footerAdditionLink = [
  { label: "Rooms", href: "/rooms/dhaka" },
  { label: "Hotels", href: "/hotels/dhaka" },
  { label: "Popular Locations", href: "/popular-locations" },
  { label: "Blogs", href: "/blogs" },
  { label: "About", href: "/about" },
];

export const noBackButtonPathsForMobileDevice = [
  "/",
  "/Homes",
  "/Rooms",
  "/Hotels",
  "/Apartments",
];
